import { Heart, Star } from '@phosphor-icons/react';
import type { Reward } from '@shh/shared';
import { Button, Icon, PointsPill, Sheet, Surface, Text } from '@shh/ui';
import { useState } from 'react';
import { cancelRequest, giveKudos, requestReward } from '../data/api';
import { availableFor, reservedFor, rewardsFor, type M } from '../data/derive';
import { useFamily, type WithId } from '../data/family';
import { iconFor } from '../lib/icons';
import { starsText } from '../i18n/stars';

const KUDOS_PRESETS = starsText.kudosPresets;

/** Reward shop of one person: request, reserve, never a dead end. */
export function RewardShop({
  member,
  unit,
  onEarnMore,
}: {
  member: M;
  unit: string;
  onEarnMore: () => void;
}) {
  const f = useFamily();
  const [confirm, setConfirm] = useState<WithId<Reward> | null>(null);
  const [error, setError] = useState<string>();
  const free = availableFor(f, member.id);
  const held = reservedFor(f, member.id);
  const list = rewardsFor(f, member.id);
  const pendingFor = (rewardId: string) =>
    f.redemptions.find((r) => r.rewardId === rewardId && r.memberId === member.id);

  if (!list.length) {
    return (
      <Surface tone="sunken">
        <Text>{starsText.noRewards}</Text>
      </Surface>
    );
  }

  return (
    <div className="flex flex-col gap-gap-lg">
      <Text tone="secondary">
        {starsText.has(member.name)}{' '}
        <strong className="text-text-primary">
          {free} {unit}
        </strong>{' '}
        {starsText.free}
        {held > 0 && starsText.reserved(String(held))}.
      </Text>
      <ul className="grid grid-cols-1 gap-gap-md sm:grid-cols-2 xl:grid-cols-3">
        {list.map((r) => {
          const pending = pendingFor(r.id);
          const missing = r.cost - free;
          return (
            <li key={r.id}>
              <Surface className="flex h-full flex-col gap-gap-md">
                <div className="flex items-center gap-gap-md">
                  <span className="grid size-touch-target shrink-0 place-items-center rounded-control bg-accent-tertiary-soft">
                    <Icon icon={iconFor(r.icon)} size="lg" className="text-accent-tertiary" />
                  </span>
                  <div className="flex flex-col">
                    <span className="font-display text-lg font-black">{r.title}</span>
                    <span className="flex items-center gap-gap-xs text-sm font-semibold text-reward-points-ink">
                      <Icon icon={Star} size="sm" weight="fill" />
                      {r.cost} {unit}
                    </span>
                  </div>
                </div>
                {pending ? (
                  <div className="mt-auto flex flex-wrap items-center justify-between gap-gap-sm">
                    <Text size="sm" tone="secondary" as="span">
                      {starsText.requested}
                    </Text>
                    <Button
                      variant="quiet"
                      onPress={() => void cancelRequest(f.householdId, pending.id)}
                    >
                      {starsText.withdraw}
                    </Button>
                  </div>
                ) : missing > 0 ? (
                  <Button variant="secondary" className="mt-auto" onPress={onEarnMore}>
                    {starsText.earnMore(String(missing), unit)}
                  </Button>
                ) : (
                  <Button className="mt-auto" onPress={() => setConfirm(r)}>
                    {starsText.redeem}
                  </Button>
                )}
              </Surface>
            </li>
          );
        })}
      </ul>

      <Sheet
        isOpen={!!confirm}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm ? starsText.redeemAsk(confirm.title) : ''}
      >
        {confirm && (
          <div className="flex flex-col gap-gap-lg">
            <Text>
              {starsText.costs}{' '}
              <strong>
                {confirm.cost} {unit}
              </strong>
              {starsText.thenLeft}{' '}
              <strong>
                {free - confirm.cost} {unit}
              </strong>
              .
            </Text>
            <Text size="sm" tone="secondary">
              {starsText.parentsApprove}
            </Text>
            {error && (
              <p role="alert" className="font-semibold text-feedback-danger">
                {error}
              </p>
            )}
            <div className="grid grid-cols-2 gap-gap-sm">
              <Button variant="secondary" onPress={() => setConfirm(null)}>
                {starsText.cancel}
              </Button>
              <Button
                onPress={() => {
                  setError(undefined);
                  requestReward(f.householdId, confirm, member.id)
                    .then(() => setConfirm(null))
                    .catch(() => setError(starsText.failed));
                }}
              >
                {starsText.yesAsk}
              </Button>
            </div>
          </div>
        )}
      </Sheet>
    </div>
  );
}

/** Familienmoment: two taps – what happened, how many stars. */
export function KudosSheet({ member, onClose }: { member: M | null; onClose: () => void }) {
  return (
    <Sheet
      isOpen={!!member}
      onOpenChange={(open) => !open && onClose()}
      title={member ? starsText.momentFor(member.name) : ''}
    >
      {member && <KudosForm key={member.id} member={member} onDone={onClose} />}
    </Sheet>
  );
}

function KudosForm({ member, onDone }: { member: M; onDone: () => void }) {
  const [text, setText] = useState<string>(KUDOS_PRESETS[0]!);
  const [points, setPoints] = useState(5);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <div className="flex flex-col gap-gap-lg">
      <fieldset className="flex flex-col gap-gap-sm">
        <legend className="mb-gap-sm font-semibold">{member.name} …</legend>
        <div className="flex flex-wrap gap-gap-sm">
          {KUDOS_PRESETS.map((p) => (
            <Button
              key={p}
              variant={p === text ? 'primary' : 'secondary'}
              aria-pressed={p === text}
              onPress={() => setText(p)}
            >
              {p}
            </Button>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-gap-sm">
        <legend className="mb-gap-sm font-semibold">{starsText.stars}</legend>
        <div className="flex flex-wrap gap-gap-sm">
          {[2, 5, 10].map((p) => (
            <Button
              key={p}
              variant={p === points ? 'primary' : 'secondary'}
              aria-pressed={p === points}
              onPress={() => setPoints(p)}
            >
              <PointsPill points={p} signed unit={starsText.stars} />
            </Button>
          ))}
        </div>
      </fieldset>
      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <Button
        size="lg"
        icon={Heart}
        isDisabled={busy}
        onPress={() => {
          setBusy(true);
          giveKudos(member.id, text, points)
            .then(onDone)
            .catch(() => {
              setError(starsText.parentMode);
              setBusy(false);
            });
        }}
      >
        {starsText.send}
      </Button>
    </div>
  );
}
